import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  afterRenderEffect,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { debounce, form, FormField } from '@angular/forms/signals';
import Collection from '@arcgis/core/core/Collection';
import Extent from '@arcgis/core/geometry/Extent';
import type { LocatorSearchSourceProperties } from '@arcgis/core/widgets/Search/LocatorSearchSource';
import type { SuggestResult } from '@arcgis/core/widgets/Search/types';
import '@arcgis/map-components/components/arcgis-search';
import { environment } from '../../../environments/environment';
import { areaLayer } from '../../layers/area.layer';
import { outagesPolygonLayer } from '../../layers/outages.layer';
import { HighlightSearchPipe } from '../../pipes/highlight-search.pipe';
import { createQueryParams } from '../../utils/query';
import { InfoContainerStore } from '../info-container/info-container.store';

@Component({
  selector: 'app-search',
  imports: [
    CdkConnectedOverlay,
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    FormField,
    HighlightSearchPipe,
    Listbox,
    Option,
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SearchComponent {
  private readonly infoContainerStore = inject(InfoContainerStore);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  private readonly combobox = viewChild.required(Combobox);
  private readonly listbox = viewChild(Listbox);
  private readonly arcgisSearchElement =
    viewChild.required<ElementRef<HTMLArcgisSearchElement>>('arcgisSearch');

  protected readonly popupExpanded = signal(false);
  protected readonly selectedSuggestions = signal<SuggestResult[]>([]);

  protected readonly sources = new Collection<LocatorSearchSourceProperties>([
    {
      zoomScale: 3000,
      filter: {
        geometry: new Extent({ xmin: 9, ymin: 58, xmax: 13, ymax: 62 }),
      },
      url: 'https://services.geodataonline.no/arcgis/rest/services/ProxyServices/Adresse/GeocodeServer',
      apiKey: environment.GEODATA_ONLINE_SEARCH_API,
      singleLineFieldName: 'SingleLine',
    },
  ]);

  private readonly model = signal({ term: '' });
  protected readonly form = form(this.model, path => {
    debounce(path.term, 200);
  });

  private readonly suggestionsResource = resource({
    params: () => {
      if (this.selectedSuggestions().length) return undefined;

      // The API seems to stop suggesting locations when including comma or period.
      const term = this.model()
        .term.trim()
        .replace(/[,.!@#$%^&*()]/g, '');

      return term || undefined;
    },
    loader: async ({ params }) => {
      try {
        return await this.arcgisSearchElement().nativeElement.suggest(params);
      } catch (error) {
        console.error('Search suggestion error:', error);
        throw error;
      }
    },
  });

  protected readonly suggestions = computed<SuggestResult[]>(() => {
    if (!this.suggestionsResource.hasValue()) return [];

    const response = this.suggestionsResource.value();
    if (!response) return [];

    return response.results[0]?.results ?? [];
  });
  protected readonly isLoading = this.suggestionsResource.isLoading;
  protected readonly hasError = computed(() => !!this.suggestionsResource.error());

  protected readonly resultsReady = computed(
    () =>
      !!this.model().term.trim() &&
      !this.selectedSuggestions().length &&
      (this.suggestionsResource.hasValue() || this.hasError()),
  );

  constructor() {
    effect(onCleanup => {
      if (!this.popupExpanded() || !this.resultsReady()) return;

      const term = this.model().term;

      if (this.hasError()) {
        void this.liveAnnouncer.announce('Det oppstod en feil under søket. Prøv igjen.', 'polite');
        return;
      }

      const count = this.suggestions().length;
      if (count > 0) {
        void this.liveAnnouncer.announce(
          `${count.toString()} adresseforslag for ${term}.`,
          'polite',
        );
        return;
      }

      void this.liveAnnouncer.announce(`Ingen adresser på søket ${term}.`, 'polite');

      onCleanup(() => {
        this.liveAnnouncer.clear();
      });
    });

    afterRenderEffect(() => {
      if (this.combobox().expanded()) {
        this.listbox()?.scrollActiveItemIntoView();
      }
    });
  }

  protected clear() {
    this.resetInfoContainer();
    this.form().reset({ term: '' });
    this.selectedSuggestions.set([]);
    this.popupExpanded.set(false);
    this.form.term().focusBoundControl();
  }

  protected onTermInput() {
    this.selectedSuggestions.set([]);
  }

  protected onCommit() {
    const suggestion = this.selectedSuggestions()[0];
    if (suggestion) {
      void this.commitSuggestion(suggestion);
    }

    this.popupExpanded.set(false);
    this.combobox().element.focus();
  }

  private async showOutageStatusFor(suggestion: SuggestResult) {
    const searchResult = await this.arcgisSearchElement().nativeElement.search(suggestion);

    const resultLocation = searchResult?.results.at(0)?.results?.at(0)?.feature.geometry;

    if (!resultLocation) return;
    const queryParams = createQueryParams(resultLocation);

    // Step 1: Check if the result is inside Elvia's area of responsibility,
    // if a features exist, the search response intersects with the gray area, so it is outside Elvia's area
    const elviaAreaResponse = await areaLayer.queryFeatures(queryParams);
    if (elviaAreaResponse.features.length) {
      this.infoContainerStore.setView('outsideElviaArea');
      this.infoContainerStore.setIsOpen(true);
      return;
    }

    // Step 2: If the result is inside Elvia's area, check for outages at that location
    const outagesResponse = await outagesPolygonLayer.queryFeatures(queryParams);
    const outageId = outagesResponse.features.at(0)?.getObjectId();

    if (outageId) {
      this.infoContainerStore.setSelectedOutage(+outageId);
      this.infoContainerStore.setView('outage');
    } else {
      this.infoContainerStore.setView('noOutage');
    }
    this.infoContainerStore.setIsOpen(true);
  }

  private async commitSuggestion(suggestion: SuggestResult) {
    this.model.set({ term: suggestion.text ?? '' });

    try {
      await this.showOutageStatusFor(suggestion);
    } catch (error) {
      console.error('Error during search:', error);
    }
  }

  protected resetInfoContainer() {
    this.infoContainerStore.resetAccordion();
  }
}
